import React from 'react';
import { getTemplateById } from './wallPostTemplates';
import { PollDisplay } from './PollDisplay';

interface TemplatePostDisplayProps {
  templateId: string;
  templateData: Record<string, string | string[]>;
  postId: string;
}

const TemplatePostDisplay: React.FC<TemplatePostDisplayProps> = ({
  templateId,
  templateData,
  postId,
}) => {
  const template = getTemplateById(templateId);

  if (!template) {
    return <p className="text-muted-foreground">Unknown template</p>;
  }

  // Find media fields (image/video)
  const mediaFields = template.fields.filter(
    (f) => f.type === 'image' || f.type === 'video'
  );
  const textFields = template.fields.filter(
    (f) => f.type !== 'image' && f.type !== 'video' && f.type !== 'poll_options'
  );
  const pollField = template.fields.find((f) => f.type === 'poll_options');
  const pollQuestionField = template.fields.find((f) => f.id === 'poll_question');

  return (
    <div className="space-y-3">
      {/* Template Label */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{template.displayEmojis[0]}</span>
        <span className="font-medium">{template.name}</span>
      </div>

      {/* Media Display */}
      {mediaFields.map((field) => {
        const url = templateData[field.id];
        if (!url || typeof url !== 'string') return null;

        if (field.type === 'video') {
          return (
            <video
              key={field.id}
              src={url}
              controls
              className="w-full max-h-[400px] rounded-lg bg-muted"
            />
          );
        }

        return (
          <img
            key={field.id}
            src={url}
            alt={field.label}
            className="w-full rounded-lg object-cover max-h-[400px]"
          />
        );
      })}

      {/* Text Fields with Emojis */}
      <div className="space-y-2">
        {textFields.map((field) => {
          const value = templateData[field.id];
          if (!value || typeof value !== 'string') return null;

          return (
            <div key={field.id} className="flex gap-2">
              <span className="text-lg flex-shrink-0">{field.emoji}</span>
              <div>
                <span className="font-medium text-sm text-muted-foreground">
                  {field.label}:
                </span>
                <p className="whitespace-pre-wrap">{value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Poll Display */}
      {pollField && pollQuestionField && templateData.poll_options && (
        <PollDisplay
          postId={postId}
          pollQuestion={templateData.poll_question as string || ''}
          pollOptions={
            Array.isArray(templateData.poll_options)
              ? templateData.poll_options
              : JSON.parse(templateData.poll_options as string || '[]')
          }
        />
      )}
    </div>
  );
};

export default TemplatePostDisplay;
